import React, { useEffect, useState } from 'react';
import { OrderDetailsDTO } from '../../types/order';
import { generateQRCodeWithLogo } from '@/utils/qrCodeWithLogo';
import logoImage from '../../assets/logo.png';

interface InvoicePrintViewProps {
  order: OrderDetailsDTO;
}

const InvoicePrintView: React.FC<InvoicePrintViewProps> = ({ order }) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);

  useEffect(() => {
    // Generate QR code for order tracking URL
    const generateQRCode = async () => {
      const orderUrl = `${window.location.origin}/orders/${order.id}`;
      try {
        const qrDataUrl = await generateQRCodeWithLogo(orderUrl, {
          width: 150,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
          logoSizeRatio: 0.2
        });
        setQrCodeDataUrl(qrDataUrl);
      } catch (_error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [order.id]);

  const formatCurrency = (amount: number | undefined) => {
    if (!amount || amount === 0) return '—';
    return `₹${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="print-container p-8 max-w-4xl mx-auto bg-white">
      <style>{`
        @media print {
          @page {
            margin: 1cm;
            size: A4;
          }
          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100%;
            height: auto;
            overflow: visible !important;
          }
          /* Hide everything except print container */
          body > *:not(.print-container-parent) {
            display: none !important;
          }
          /* Show print container parent */
          .print-container-parent {
            position: static !important;
            display: block !important;
            width: 100% !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: visible !important;
            z-index: auto !important;
          }
          .print-container {
            position: relative !important;
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            margin: 0 !important;
            padding: 1cm !important;
            background: white !important;
            box-shadow: none !important;
            overflow: visible !important;
          }
          .print-container * {
            visibility: visible !important;
          }
          .no-print {
            display: none !important;
          }
          /* Prevent page breaks inside sections */
          .print-container > div {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          /* Prevent page breaks after header */
          .invoice-header {
            page-break-after: avoid;
            break-after: avoid;
          }
          /* Ensure tables don't break across pages */
          table {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
        .print-container {
          font-family: Arial, sans-serif;
          color: #000;
        }
      `}</style>

      {/* Header */}
      <div className="mb-8 pb-6 border-b-2 border-gray-800 invoice-header">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <img 
              src={logoImage} 
              alt="Master Ji Logo" 
              className="w-16 h-16 object-contain"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Master Ji</h1>
              <p className="text-sm text-gray-600">Digital Tailoring Marketplace</p>
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">INVOICE</h2>
            <p className="text-sm text-gray-600">Order #{order.id}</p>
            <p className="text-sm text-gray-600">Date: {formatDate(order.createdAt)}</p>
          </div>
        </div>
      </div>

      {/* Customer & Tailor Info */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Bill To:</h3>
          <p className="text-sm text-gray-700">{order.customer.name}</p>
          {order.customer.phone && (
            <p className="text-sm text-gray-700">{order.customer.phone}</p>
          )}
        </div>
        {order.assignedTailor && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Tailor:</h3>
            <p className="text-sm text-gray-700">{order.assignedTailor.name}</p>
          </div>
        )}
      </div>

      {/* Pickup/Delivery Details */}
      {(order.dueAt || order.logistics?.method) && (
        <div className="mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Pickup/Delivery Details</h3>
          <div className="bg-gray-50 p-4 rounded">
            <div className="space-y-2">
              {order.logistics?.method && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Method:</span>
                  <span className="font-medium text-gray-900">
                    {order.logistics.method === 'PICKUP' ? 'Pickup' : 'Delivery'}
                  </span>
                </div>
              )}
              {order.dueAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {order.logistics?.method === 'DELIVERY' ? 'Delivery Date:' : 'Pickup Date:'}
                  </span>
                  <span className="font-medium text-gray-900">{formatDate(order.dueAt)}</span>
                </div>
              )}
              {order.logistics?.address && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Address:</span>
                  <span className="font-medium text-gray-900 text-right max-w-xs">{order.logistics.address}</span>
                </div>
              )}
              {order.logistics?.trackingCode && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tracking Code:</span>
                  <span className="font-medium text-gray-900 font-mono">{order.logistics.trackingCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Items */}
      <div className="mb-8">
        <h3 className="font-semibold text-gray-900 mb-4">Order Details</h3>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Item</th>
              <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Description</th>
              <th className="border border-gray-300 px-4 py-2 text-right text-sm font-semibold">Quantity</th>
              <th className="border border-gray-300 px-4 py-2 text-right text-sm font-semibold">Price</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((item, index) => (
              <tr key={item.id || index}>
                <td className="border border-gray-300 px-4 py-2 text-sm">{item.garmentType}</td>
                <td className="border border-gray-300 px-4 py-2 text-sm">
                  {item.fabric && `${item.fabric}`}
                  {item.clothType && item.clothType !== item.fabric && ` - ${item.clothType}`}
                </td>
                <td className="border border-gray-300 px-4 py-2 text-right text-sm">{item.quantity}</td>
                <td className="border border-gray-300 px-4 py-2 text-right text-sm">
                  {item.unitPrice ? formatCurrency(item.unitPrice) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Measurements */}
      {order.measurementSet && order.measurementSet.fieldsPreview.length > 0 && (
        <div className="mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Measurements</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {order.measurementSet.fieldsPreview.map((field) => (
              <div key={field.key} className="flex justify-between border-b border-gray-200 pb-1">
                <span className="text-gray-600">{field.label}:</span>
                <span className="font-medium">{field.value}"</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price Summary */}
      {order.price && (
        <div className="mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Price Summary</h3>
          <div className="bg-gray-50 p-4 rounded">
            <div className="space-y-2">
              {order.price.materials && order.price.materials > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Materials:</span>
                  <span>{formatCurrency(order.price.materials)}</span>
                </div>
              )}
              {order.price.labor && order.price.labor > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Labor:</span>
                  <span>{formatCurrency(order.price.labor)}</span>
                </div>
              )}
              {order.price.discount && order.price.discount > 0 && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>Discount:</span>
                  <span>-{formatCurrency(order.price.discount)}</span>
                </div>
              )}
              {order.price.total && order.price.total > 0 && (
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300 mt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(order.price.total)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Notes */}
      {order.notes?.customerNote && (
        <div className="mb-8">
          <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
          <p className="text-sm text-gray-700">{order.notes.customerNote}</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-gray-300">
        <div className="flex justify-between items-start">
          <div className="flex flex-col items-start">
            {qrCodeDataUrl && (
              <div className="mb-2">
                <img 
                  src={qrCodeDataUrl} 
                  alt="Order Tracking QR Code" 
                  className="w-24 h-24"
                />
                <p className="text-xs text-gray-500 mt-1">Scan to track order</p>
              </div>
            )}
          </div>
          <div className="text-center text-xs text-gray-500 flex-1">
            <p className="font-semibold mb-1">Thank you for choosing Master Ji!</p>
            <p>This is a computer-generated invoice.</p>
            <p className="mt-2">For any queries, please contact us at infotherealmasterji@gmail.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrintView;

